import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import clearTypename from 'graphql/clearTypename'
import {
  useCidadaoEditQuery,
  useCreateCidadaoCadsusMutation,
  useSincronizaCidadaoCadsusPollingQuery,
} from 'graphql/hooks.generated'
import { SincronizacaoCadsusResultEnum } from 'graphql/types.generated'
import useAtmosphere from 'hooks/useAtmosphere'
import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ModalBuscaInformacoesContentState } from 'view/cidadao/create/cadsus/components/ModalBuscaInformacoesCadsus'
import { ResultSyncCadsusType } from 'view/cidadao/create/cadsus/types/model-cadsus'
import { useNeedLoginGovBrToCadsus } from 'view/cidadao/hooks/useNeedLoginGovBrToCadsus'

import { convertCadsusCidadaoLocalTypeToFormModel } from '../../converter-cidadao'
import { CidadaoFormModel } from '../../types/model-cidadao'
import { CadsusAtmosphereResponse } from './useCadsusCreateView'

export interface InfosExcluiveisCadsus {
  telefoneResidencial: string
  telefoneCelular: string
  telefoneContato: string
  nomeSocial: string
}

export interface UseCadsusFciEditViewProps {
  cidadaoId: string
}

let pollingSincronizacaoCadsus = false
let pollingBuscaCidadaoAtualizadoCadsus = true

const POLLING_INTERVAL_TIME_5S = 5 * 1000 // 5 segundos

export function useCadsusFciEditView(props: UseCadsusFciEditViewProps) {
  const { cidadaoId } = props
  const { needLoginGovBr, hasAllConfigs } = useNeedLoginGovBrToCadsus()
  const { data: sessao } = useSession()
  const handleRejection = useErrorHandler()

  const [uuidBuscaCidadaoAtualizado] = useState<string>(uuidv4())
  const [saveCadsus] = useCreateCidadaoCadsusMutation()
  const [isModalSincronizacaoLoading, setIsModalSincronizacaoLoading] = useState<boolean>(false)
  const [cadsusSyncResult, setCadsusSyncResult] = useState<ResultSyncCadsusType>({ uuid: '' })
  const [isModalSincronizacaoOpen, setIsModalSincronizacaoOpen] = useState<boolean>(false)
  const [uuidSincronizacaoCadsus, setUuidSincronizacaoCadsus] = useState<string>()
  const [hasCnsState, setHasCnsState] = useState(false)
  const [dadosExcluiveisState, setDadosExcluiveisState] = useState<InfosExcluiveisCadsus>({
    telefoneCelular: null,
    telefoneContato: null,
    telefoneResidencial: null,
    nomeSocial: null,
  })
  const [cidadaoCadsus, setCidadaoCadsus] = useState<CidadaoFormModel>(null)
  const [isModalBuscaCidadaoOpenState, setIsModalBuscaCidadaoOpenState] = useState<boolean>(false)
  const [resultBuscaInfoCidadaoState, setResultBuscaInfoCidadaoState] = useState<ModalBuscaInformacoesContentState>({
    isLoading: false,
  })
  const [isCadsusHabilitado, setIsCadsusHabilitado] = useState<boolean>(false)

  useEffect(() => {
    if (hasAllConfigs) {
      setIsCadsusHabilitado(true)
    }
  }, [hasAllConfigs])

  useEffect(() => {
    if (isCadsusHabilitado) {
      setIsModalBuscaCidadaoOpenState(true)
      setResultBuscaInfoCidadaoState((prev) => ({
        ...prev,
        isLoading: true,
      }))
    }
  }, [isCadsusHabilitado])

  const atualizaResultadoBuscaCidadaoCadsus = useCallback(
    (data: ResultSyncCadsusType) => {
      if (!isCadsusHabilitado) return

      pollingBuscaCidadaoAtualizadoCadsus = false

      if (
        data.result === SincronizacaoCadsusResultEnum.SUCESSO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_CADSUS_DESABILITADO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_SEM_CONEXAO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_CERTIFICADO_DESABILITADO
      ) {
        setIsModalBuscaCidadaoOpenState(false)
      } else {
        setResultBuscaInfoCidadaoState({
          cns: data.cidadaoCadsus?.cns,
          cpf: data.cidadaoCadsus?.cpf,
          errors: data.errors,
          statusSincronizacao: data.result,
          isLoading: false,
        })
      }

      setCidadaoCadsus(
        convertCadsusCidadaoLocalTypeToFormModel(
          clearTypename(data.cidadaoLocal),
          data?.cidadaoCadsus?.cpf,
          data?.cidadaoCadsus?.cns
        )
      )

      setDadosExcluiveisState({
        telefoneCelular: data.cidadaoLocal.telefoneCelular,
        telefoneContato: data.cidadaoLocal.telefoneContato,
        telefoneResidencial: data.cidadaoLocal.telefoneResidencial,
        nomeSocial: data.cidadaoLocal.nomeSocial,
      })
    },
    [isCadsusHabilitado]
  )

  useAtmosphere<CadsusAtmosphereResponse>({
    topic: `cadsus-cidadao-atualizado/${sessao.profissional.usuario.id}`,
    onMessage: (responseBody) => {
      if (responseBody.uuid === uuidBuscaCidadaoAtualizado && pollingBuscaCidadaoAtualizadoCadsus) {
        refetchSincronizacaoPolling({ uuid: uuidBuscaCidadaoAtualizado })
          .then(
            ({ data }) =>
              !!data?.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidBuscaCidadaoAtualizado &&
              pollingBuscaCidadaoAtualizadoCadsus &&
              atualizaResultadoBuscaCidadaoCadsus(data.sincronizacaoCadsusPolling)
          )
          .catch(handleRejection)
      }
    },
  })

  useCidadaoEditQuery({
    variables: { input: { cidadao: cidadaoId, uuid: uuidBuscaCidadaoAtualizado } },
    skip: !isCadsusHabilitado,
  })

  const atualizacaoResultadoSincronizacaoCadsus = (data: ResultSyncCadsusType): void => {
    pollingSincronizacaoCadsus = false
    setCadsusSyncResult(data)
    setIsModalSincronizacaoLoading(false)
  }

  useAtmosphere<CadsusAtmosphereResponse>({
    topic: `cadsus-sincronizacao-completa/${sessao.profissional.usuario.id}`,
    onMessage: (responseBody) => {
      if (responseBody.uuid === uuidSincronizacaoCadsus && pollingSincronizacaoCadsus) {
        refetchSincronizacaoPolling({ uuid: uuidSincronizacaoCadsus })
          .then(
            ({ data }) =>
              !!data?.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidSincronizacaoCadsus &&
              pollingSincronizacaoCadsus &&
              atualizacaoResultadoSincronizacaoCadsus(data.sincronizacaoCadsusPolling)
          )
          .catch(handleRejection)
      }
    },
  })

  const { refetch: refetchSincronizacaoPolling } = useSincronizaCidadaoCadsusPollingQuery({ skip: true })

  useEffect(() => {
    if (!isCadsusHabilitado) return
    let shouldUpdateSincronizacao = true

    const scheduleCadsusSincronizacaoPolling = setInterval(() => {
      if (!!uuidSincronizacaoCadsus && pollingSincronizacaoCadsus && shouldUpdateSincronizacao) {
        shouldUpdateSincronizacao = false
        refetchSincronizacaoPolling({ uuid: uuidSincronizacaoCadsus })
          .then(
            ({ data }) =>
              !!data?.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidSincronizacaoCadsus &&
              pollingSincronizacaoCadsus &&
              atualizacaoResultadoSincronizacaoCadsus(data.sincronizacaoCadsusPolling)
          )
          .finally(() => (shouldUpdateSincronizacao = true))
          .catch(handleRejection)
      }
    }, POLLING_INTERVAL_TIME_5S)

    return () => clearInterval(scheduleCadsusSincronizacaoPolling)
  }, [handleRejection, isCadsusHabilitado, refetchSincronizacaoPolling, uuidSincronizacaoCadsus])

  useEffect(() => {
    if (!isCadsusHabilitado) return
    let shouldUpdateBuscaCidadaoCadsus = true

    const scheduleBuscaCidadaoCadsusPolling = setInterval(() => {
      if (!!uuidBuscaCidadaoAtualizado && pollingBuscaCidadaoAtualizadoCadsus && shouldUpdateBuscaCidadaoCadsus) {
        shouldUpdateBuscaCidadaoCadsus = false
        refetchSincronizacaoPolling({ uuid: uuidBuscaCidadaoAtualizado })
          .then(
            ({ data }) =>
              !!data?.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidBuscaCidadaoAtualizado &&
              pollingBuscaCidadaoAtualizadoCadsus &&
              atualizaResultadoBuscaCidadaoCadsus(data.sincronizacaoCadsusPolling)
          )
          .finally(() => (shouldUpdateBuscaCidadaoCadsus = true))
          .catch(handleRejection)
      }
    }, POLLING_INTERVAL_TIME_5S)
    return () => {
      clearInterval(scheduleBuscaCidadaoCadsusPolling)
      pollingBuscaCidadaoAtualizadoCadsus = true
    }
  }, [
    atualizaResultadoBuscaCidadaoCadsus,
    handleRejection,
    isCadsusHabilitado,
    refetchSincronizacaoPolling,
    uuidBuscaCidadaoAtualizado,
  ])

  const syncCadsus = (id: string, cns: string) => {
    if (!isCadsusHabilitado) return
    setCadsusSyncResult(null)
    const hash = uuidv4()
    setUuidSincronizacaoCadsus(hash)
    pollingSincronizacaoCadsus = true
    saveCadsus({
      variables: {
        input: {
          cidadao: id,
          telefoneCelularAntigo: dadosExcluiveisState.telefoneCelular,
          telefoneContatoAntigo: dadosExcluiveisState.telefoneContato,
          telefoneResidencialAntigo: dadosExcluiveisState.telefoneResidencial,
          nomeSocialAntigo: dadosExcluiveisState.nomeSocial,
          uuid: hash,
        },
      },
    })
    setIsModalSincronizacaoLoading(true)
    setIsModalSincronizacaoOpen(true)
    setHasCnsState(!!cns)
  }

  const showSyncCadsusButton = isCadsusHabilitado && !!cidadaoCadsus?.dadosPessoais.cns

  return {
    cidadaoCadsus,
    showSyncCadsusButton,
    setCidadaoCadsus,
    needLoginGovBr,
    isCadsusHabilitado,
    syncCadsus,
    resultBuscaInfoCidadaoState,
    isModalBuscaCidadaoOpenState,
    setIsModalBuscaCidadaoOpenState,
    isModalSincronizacaoLoading,
    cadsusSyncResult,
    isModalSincronizacaoOpen,
    setIsModalSincronizacaoOpen,
    hasCnsState,
  }
}
